/**
 * Client for the tenant-scoped legal change service — /:customer/legal/changes and
 * /:customer/legal/change-feeds on tri-server.
 *
 * Read changes — all of them (default), narrowed by filters, only those touching the tenant's
 * activated obligations, or a feed's scope — and manage the tenant's RSS feed subscriptions.
 * Creating and revoking a feed needs the same right as managing bindings; the server answers 403
 * otherwise. The feed URL carries an access key and is returned exactly once, by the create call.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

/** `null` = Bund (federal law); otherwise the two-letter Land code, e.g. `BW`. */
export type LandCode =
  | "BW"
  | "BY"
  | "BE"
  | "BB"
  | "HB"
  | "HH"
  | "HE"
  | "MV"
  | "NI"
  | "NW"
  | "RP"
  | "SL"
  | "SN"
  | "ST"
  | "SH"
  | "TH";

/** The scope selector value for federal law in the feed dialog (the change items carry `null`). */
export const BUND = "BUND";

export const LAENDER: LandCode[] = [
  "BW",
  "BY",
  "BE",
  "BB",
  "HB",
  "HH",
  "HE",
  "MV",
  "NI",
  "NW",
  "RP",
  "SL",
  "SN",
  "ST",
  "SH",
  "TH",
];

/** `all` = every legal change in the catalog (default); activated obligations are portfolio/site specific. */
export type ChangeFeedScopeMode = "all" | "activatedObligations" | "custom";

export interface ChangeFeedScope {
  mode: ChangeFeedScopeMode;
  regulationIds?: string[];
  /** Land codes; `BUND` for federal law. */
  laender?: string[];
  documentId?: string;
  regulatedObjectCodes?: string[];
}

export interface ChangeFeed {
  id: string;
  name: string;
  scope: ChangeFeedScope;
  scopeLabel: string;
  createdAt: string;
  lastFetchedAt: string | null;
  revoked: boolean;
}

export interface CreateChangeFeedResponse {
  feed: ChangeFeed;
  /** Contains the access key — shown once, never again retrievable. */
  feedUrl: string;
}

export type ChangedProvisionKind = "substantive" | "editorial" | "repealed" | "new";

export interface ChangedProvision {
  provisionId: string;
  kind: ChangedProvisionKind;
  summary: string;
  url: string;
  synopsisUrl: string;
}

export type ChangeStatus = "scheduled" | "inForce";

export interface ChangeItem {
  guid: string;
  regulationId: string;
  /** Short name, e.g. `BetrSichV`. */
  regulation: string;
  regulationTitle: string;
  land: LandCode | null;
  /** The amending act. */
  act: string;
  /** Promulgation reference, e.g. `BGBl. 2026 I Nr. 123`. */
  citation: string;
  /** YYYY-MM-DD */
  effectiveDate: string;
  status: ChangeStatus;
  provisions: ChangedProvision[];
  /** Obligations derived from the amended provisions (catalog-wide). */
  impactedObligations: number;
  /** Of those, how many the tenant has activated. */
  activatedObligations: number;
  /** Of those, how many are reviewed (confirmed) — information only, the review status filters nothing. */
  confirmedObligations?: number;
  officialUrl: string;
  portalUrl: string;
}

/** A regulation with recorded changes — option for the regulation filter. */
export interface ChangeRegulation {
  eliId: string;
  shortName: string;
  title: string | null;
  /** Land code, null for federal/EU law. */
  land: string | null;
}

/** A guideline the tenant can see that is linked to the legal corpus (e.g. GEFMA 190). */
export interface ChangeFeedDocument {
  id: string;
  title: string;
  date: string | null;
}

export interface ChangesResponse {
  items: ChangeItem[];
  scopeLabel: string;
}

/** Filters of the change list — the same fields as a custom feed scope. */
export interface ChangeFilters {
  regulationIds?: string[];
  /** Land codes; `BUND` for federal law. */
  laender?: string[];
  documentId?: string;
  regulatedObjectCodes?: string[];
}

export type ChangesQuery =
  | (ChangeFilters & {
      /** Set only for “only my activated obligations”; omitted = all changes. */
      mode?: "activatedObligations";
      since?: string;
      limit?: number;
    })
  | { feedId: string; since?: string; limit?: number };

const processResponse = async (response: Response) => {
  if (response.ok) {
    // DELETE may answer 204 without a body.
    if (response.status === 204) {
      return null;
    }

    const text = await response.text();

    return text ? JSON.parse(text) : null;
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage, response.status);
};

export const useLegalChangesAPI = (customer: string) => {
  const baseUrl = `${configuration.serverUrl}/${customer}/legal`;

  const request = async (method: "GET" | "POST" | "DELETE", path: string, body?: unknown) => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      headers: { "Content-Type": "application/json" },
    });

    return processResponse(response);
  };

  return {
    getChangeFeeds: async (): Promise<ChangeFeed[]> => {
      const result = await request("GET", "/change-feeds");

      return result?.feeds || [];
    },
    getChangeRegulations: async (): Promise<ChangeRegulation[]> => {
      const result = await request("GET", "/change-feeds/regulations");

      return result?.regulations || [];
    },
    getChangeFeedDocuments: async (): Promise<ChangeFeedDocument[]> => {
      const result = await request("GET", "/change-feeds/documents");

      return result?.documents || [];
    },
    createChangeFeed: (name: string, scope: ChangeFeedScope): Promise<CreateChangeFeedResponse> =>
      request("POST", "/change-feeds", { name, scope }),
    revokeChangeFeed: (feedId: string): Promise<unknown> =>
      request("DELETE", `/change-feeds/${encodeURIComponent(feedId)}`),
    getChanges: async (query: ChangesQuery): Promise<ChangesResponse> => {
      const params = new URLSearchParams();

      if ("feedId" in query) {
        params.set("feedId", query.feedId);
      } else {
        if (query.mode) params.set("mode", query.mode);
        if (query.regulationIds?.length) params.set("regulationIds", query.regulationIds.join(","));
        if (query.laender?.length) params.set("laender", query.laender.join(","));
        if (query.documentId) params.set("documentId", query.documentId);
        if (query.regulatedObjectCodes?.length) {
          params.set("regulatedObjectCodes", query.regulatedObjectCodes.join(","));
        }
      }
      if (query.since) params.set("since", query.since);
      if (query.limit) params.set("limit", String(query.limit));

      const search = params.toString();
      const result = await request("GET", search ? `/changes?${search}` : "/changes");

      return { items: result?.items || [], scopeLabel: result?.scopeLabel || "" };
    },
  };
};
